import { Global, Location as CmsLocation, SpecialMonth } from "@/lib/directus-schema";
import { AppState } from "@/types/enums";
import type { TeamSize, TeamSizeThresholds } from "@/types/teamSize";
import { makeAutoObservable } from "mobx";
import type { DailyQuestionPreview } from "@/lib/dailyQuestionPreview";
import type { HighscoreItem } from "@/lib/highscoresClient";

export type ConsentState = "unknown" | "accepted" | "rejected";

export type ModalKey =
    | "share"
    | "cookie"
    | "settings"
    | "answerResponse"
    | "closeQuestion"
    | "stats"
    | "highscores"
    | "pwaInstall"
    | "pwaInstallHelp"
    | "pwaInstallDone"
    | "pwaBonus"
    | "streakBreak"
    | "answerOverMidnight"
    | "notification"
    | "teamPoints"
    | "veryImportantQuestion"
    | "specialQuestion"
    | "feedback"
    | "legalImprint"
    | "legalDataPrivacy"
    | "departmentInfo"
    | "teamSelection"
    | "modalTemp"
    | "patchNotes"
    | "scheduledModal"
    ;

export type FeedbackModalVariant = "feedback" | "submit_question";

export type SettingsInitialTab =
    | "tutorial_tab"
    | "submit_question_tab"
    | "notifications_tab"
    | "team_tab"
    | "feedback_tab"
    | "prizes_tab";


export type CookieModalMode = "default" | "settings";

export type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export class Store {

    pwaDeferredPrompt: BeforeInstallPromptEvent | null = null;
    pwaIsStandalone = false;
    pwaIsIOS = false;
    pwaInstallAfterFirstAnswerPending = false;

    currentState: AppState = AppState.START;

    startStatusLoading: boolean = true;

    // Loaded during the splash (HomeClient.tsx), null: no question today or the request failed
    dailyQuestionPreview: DailyQuestionPreview | null = null;

    // Start screen highscore list, loaded during the splash (HomeClient.tsx) and refreshed by Highscore.tsx, null: not loaded yet
    highscorePreview: HighscoreItem[] | null = null;

    cookiesAccepted: boolean | null = null;

    answerCorrect: boolean | undefined = false;

    modals: Record<ModalKey, boolean> = {
        share: false,
        cookie: false,
        settings: false,
        answerResponse: false,
        closeQuestion: false,
        stats: false,
        highscores: false,
        pwaInstall: false,
        pwaInstallHelp: false,
        pwaInstallDone: false,
        pwaBonus: false,
        streakBreak: false,
        answerOverMidnight: false,
        notification: false,
        teamPoints: false,
        veryImportantQuestion: false,
        specialQuestion: false,
        feedback: false,
        legalImprint: false,
        legalDataPrivacy: false,
        departmentInfo: false,
        teamSelection: false,
        modalTemp: false,
        patchNotes: false,
        scheduledModal: false
    };

    department: string = "";
    locations: CmsLocation[] = [];
    selectedLocationId: number | null = null;
    customerSlug: string = ""
    language: "de" | "en" = "de";

    globalData: Global | null = null;
    specialMonthData: SpecialMonth | null = null;

    consent: ConsentState = "unknown";
    firstVisit: boolean = false;

    teamId: number | null = null;
    feedbackModalVariant: FeedbackModalVariant = "feedback";
    settingsInitialTab: SettingsInitialTab | null = null;
    // Called once the settings are fully open, e.g. to close the modal they were opened from
    settingsOnOpened: (() => void) | null = null;
    cookieModalMode: CookieModalMode = "default";

    pointsAwarded: number | null = null;
    currentQuestionExplanation: string | null = null;
    currentQuestionAskNowLink: string | null = null;
    // Daily question currently shown in Question.tsx (sent with "Quiz verlassen" to detect the midnight case)
    currentDailyQuestionId: number | null = null;
    // Date (YYYY-MM-DD, Berlin) of that daily question
    currentDailyQuestionDate: string | null = null;
    pointsBreakdown: null | {
        questionPoints: number;
        streakBonus: number;
        timeBonus: number;
        pwaBonus: number;
        specialBonus: number;
        specialQuestionsPlayed: number;
        specialQuestionsTotal: number;
        isSpecialQuestion: boolean;
        teamPointsAwarded: number;
        teamMembersCount: number;
        // First game: only personal points, the player counts for the team from the next game on
        teamCountsFromNextGame?: boolean;
        // The team's points of today are above its daily cap (settled after the day)
        teamPeakDay?: boolean;
        // Size category of the team (small/medium/large/xlarge), explains why team points are high or low
        teamSize?: TeamSize | null;
        // Limits of the categories in players per day (small, medium, large), for the text in the team size tile
        teamSizeThresholds?: TeamSizeThresholds | null;
    } = null;
    // The last answer was the player's very first one (player_answers_total was 0): end screen without team points
    isFirstGame: boolean = false;

    alreadyAnswered: boolean = false;
    // When today's question was answered or left (ms since epoch); keeps the post played title of that moment.
    answeredAtMs: number | null = null;

    userStats: null | {
        pointsTotal: number;
        answersTotal: number;
        answersCorrect: number;
        hitRatePercent: number;
        streakCurrent: number;
        teamPointsTotal: number;
        streakBest: number;
        fastestResponseMs: number | null;
        fastestResponseSec: number | null;
        teamPromotionsEnabled: number;
        lastAnswerDate: string | null;
        specialQuestionsAnsweredRight: number;
    } = null;

    userStatsLoading = false;

    constructor() {
        makeAutoObservable(this);
    }

    setPwaDeferredPrompt(e: BeforeInstallPromptEvent | null) {
        this.pwaDeferredPrompt = e;
    }

    setPwaPlatform(flags: { isStandalone: boolean; isIOS: boolean }) {
        this.pwaIsStandalone = flags.isStandalone;
        this.pwaIsIOS = flags.isIOS;
    }

    setPwaInstallAfterFirstAnswerPending(value: boolean) {
        this.pwaInstallAfterFirstAnswerPending = value;
    }

    setState(state: AppState) {
        this.currentState = state;
    }

    setDailyQuestionPreview(preview: DailyQuestionPreview | null) {
        this.dailyQuestionPreview = preview;
    }

    setHighscorePreview(items: HighscoreItem[] | null) {
        this.highscorePreview = items;
    }

    setStartStatusLoading(status: boolean) {
        this.startStatusLoading = status;
    }

    setCookiesAccepted(newValue: boolean) {
        this.cookiesAccepted = newValue
    }

    setAnswerCorrect(newValue: boolean) {
        this.answerCorrect = newValue
    }

    isOpen(key: ModalKey) {
        return this.modals[key];
    }

    open(key: ModalKey) {
        this.modals[key] = true;
    }

    openCookieSettings() {
        this.cookieModalMode = "settings";
        this.open("cookie");
    }

    openSettings(tab?: SettingsInitialTab, onOpened?: () => void) {
        this.settingsInitialTab = tab ?? null;

        // Already open (e.g. underneath the slider opened from the menu): it only switches the tab, no entry animation
        if (this.modals.settings) {
            this.settingsOnOpened = null;
            onOpened?.();
            return;
        }

        this.settingsOnOpened = onOpened ?? null;
        this.open("settings");
    }

    consumeSettingsOnOpened() {
        const next = this.settingsOnOpened;
        this.settingsOnOpened = null;
        return next;
    }

    consumeSettingsInitialTab() {
        const next = this.settingsInitialTab;
        this.settingsInitialTab = null;
        return next;
    }

    close(key: ModalKey) {
        this.modals[key] = false;

        if (key === "settings") {
            this.settingsInitialTab = null;
            this.settingsOnOpened = null;
        }

        if (key === "cookie") {
            this.cookieModalMode = "default";
        }
    }

    closeAll() {
        (Object.keys(this.modals) as ModalKey[]).forEach((k) => (this.modals[k] = false));
    }

    setSelectedLocationId(id: number | null) {
        this.selectedLocationId = id;
    }

    setSelectedDepartment(slug: string) {
        this.department = slug;
    }

    setLanguage(lang: "de" | "en") {
        this.language = lang;
    }

    setConsent(v: ConsentState) {
        this.consent = v;
    }

    get isSpecialMonth() {
        return this.specialMonthData != null;
    }

    get specialMonthTranslation() {
        return this.specialMonthData?.translations?.[0] ?? null;
    }

    setSpecialMonthData(value: SpecialMonth | null) {
        this.specialMonthData = value;
    }

    setFirstVisit(v: boolean) {
        this.firstVisit = v;
    }

    setTeamId(id: number) {
        this.teamId = id;
    }

    setFeedbackModalVariant(variant: FeedbackModalVariant) {
        this.feedbackModalVariant = variant;
    }

    setPointsAwarded(points: number) {
        this.pointsAwarded = points;
    }

    setCurrentQuestionExplanation(explanation: string | null) {
        this.currentQuestionExplanation = explanation;
    }

    setCurrentQuestionAskNowLink(link: string | null) {
        this.currentQuestionAskNowLink = link;
    }

    setCurrentDailyQuestion(id: number | null, date: string | null) {
        this.currentDailyQuestionId = id;
        this.currentDailyQuestionDate = date;
    }

    setPointsBreakdown(value: Store["pointsBreakdown"]) {
        this.pointsBreakdown = value;
    }

    setIsFirstGame(value: boolean) {
        this.isFirstGame = value;
    }

    setAlreadyAnswered = (value: boolean) => {
        this.alreadyAnswered = value;
    }

    setAnsweredAtMs = (value: number | null) => {
        this.answeredAtMs = value;
    }

    setUserStats(stats: Store["userStats"]) {
        this.userStats = stats;
    }

    setUserStatsLoading(loading: boolean) {
        this.userStatsLoading = loading;

    }
}

const store = new Store()
export type StoreType = Store
export default store
